import { environment } from 'src/environments/environment';
import { Component, OnInit, NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { RoutingService } from './app.routing';
import {
  PhoneService,
  PhoneCall,
  PhoneConfigService,
  ServiceWorkerService,
  ScreenLockService
} from '@wephone-app-phone/services';
import { MessageService, DialogService, LoggerService, sleepAsync, ToastService } from '@wephone-utils';
import { MyActiveCallsComponent } from '@wephone-app-phone/call/my-active-calls/my-active-calls';
import { MatDialogRef } from '@angular/material/dialog';
import { _tk, _ti, TranslationService } from '@wephone-translation';
import { AudioDeviceService } from './services/audio-devices.service';
import { ElectronService } from 'ngx-electron';
import { CommonCustomIconsService } from '@wephone-common/service';
import { CallEvaluationService } from './services/call-evaluation.service';
import { FlexIvrSettings } from '@wephone-core/wephone-core.module';
import { SwPush } from '@angular/service-worker';
import { NotificationService } from '@wephone-core/service/notification.service';
import { EnterpriseParamDict } from '@wephone-core/system';
import { FlexBaseComponent } from '@wephone-core-ui';
import { NavigationError, Router } from '@angular/router';

@Component({
  selector: 'app-root',
  template: '<router-outlet></router-outlet>',
  styleUrls: ['./app.component.scss']
})
export class AppComponent extends FlexBaseComponent implements OnInit {
  callDialog: MatDialogRef<any, any>;
  // isProjectPhoneOnWeb: boolean;
  /*
    @HostBinding('class.web-phone-wrapper') public get phoneWrapper(): boolean {
      return this.isProjectPhoneOnWeb;
    }
  */
  constructor(
    private readonly phoneService: PhoneService,
    // private readonly translate: TranslateService,
    translationService: TranslationService,
    private readonly routerService: RoutingService, // not remove
    private readonly router: Router,
    private readonly messageService: MessageService,
    private readonly dialogService: DialogService,
    audioDeviceService: AudioDeviceService,
    private readonly logger: LoggerService,
    private readonly electron: ElectronService,
    private readonly ngZone: NgZone,
    private readonly settings: FlexIvrSettings,
    private readonly commonIconService: CommonCustomIconsService,
    private readonly evaluationService: CallEvaluationService,
    private readonly appConfigService: PhoneConfigService,
    private readonly swPush: SwPush,
    private serviceWorkerService: ServiceWorkerService,
    private screenLocker: ScreenLockService,
    private readonly notificationService: NotificationService,
    private readonly toast: ToastService
  ) {
    super();
    translationService.setDefaultLang();

    appConfigService.init();

    // Register for phone protocol handler
    const makeCallUrl = `${window.location.protocol}//${window.location.host}/phone/makecall/%s`;
    try {
      navigator.registerProtocolHandler('tel', makeCallUrl, 'Graam Phone (pwa)');
    } catch (e) {
      this.loggerService.error('Unable to register as protocol handler for tel:', e);
    }
    /*
        this.loggerService.log('registering web+graam protocol for ', makeCallUrl);
        try {
          navigator.registerProtocolHandler(
            'web+graam', makeCallUrl, 'Graam Phone (pwa)');
        } catch (e) {
          this.loggerService.error('Unable to register as protocol handler for web+graam:', e);
        }
    */

    this.addSubscription(
      messageService.subscribe('phone:call_start', (call: PhoneCall) => {
        if (!this.callDialog) {
          this.callDialog = this.dialogService.openModal(
            MyActiveCallsComponent,
            {
              disableClose: true,
              size: 'l'
            },
            ret => {
              this.callDialog = undefined;
            }
          );
        }

        sleepAsync(1000).then(
          // Wait 1 second before launching external app to make sure that call information is obtained from push-api (mostly for calling_number)
          () => {
            phoneService.runExternalAppOnNewCall(call);
          }
        );

        /*Temporarily disable inbound call notification until we implement the action handler and
         * automatically close the notificaiton when the call is hangup or anwsered
         */
        if (!document.hasFocus()) {
          if (call.isInbound()) {
            this.displayInboundCallNotification(call);
          } else {
            this.displayOutboundCallNotification(call);
          }
        }

        this.screenLocker.requestLock();
      })
    );

    this.addSubscription(
      messageService.subscribe('phone:call_end', (call: PhoneCall) => {
        if (!this.phoneService.hasCall() && this.callDialog) {
          this.callDialog.close();
        }
  
        const enterpriseParams: EnterpriseParamDict = this.appConfigService.getEnterpriseParams();
  
        if (call.isAnswered() && call.hasCallData() && enterpriseParams.call_quality_enable) {
          this.evaluationService.endCallOpenCallQualityEvaluation(call);
        }
  
        navigator.serviceWorker.getRegistration().then((reg: ServiceWorkerRegistration) => {
          if (!reg) {
            this.loggerService.error('No service worker resgistered');
            return;
          }
          reg.getNotifications().then((notifications: Notification[]) => {
            notifications.forEach((notification: Notification) => {
              notification.close();
            });
          });
        });
        this.screenLocker.releaseLock();
      })
    );

    if (electron.ipcRenderer) {
      electron.ipcRenderer.on('message:dispatch', (event, message, params) => {
        this.broadcastMessage(message, params);
      });
    }

    audioDeviceService.init();
    this.commonIconService.registerCustomIcons();
    serviceWorkerService.initServiceWorker();
    this.askNotificationPermission();

    this.addSubscription(
      this.swPush.notificationClicks.subscribe((click: any) => {
        const call = this.phoneService.getActiveCall();
        if (click.action === 'answer') {
          if (call && call.canAnswerHere()) {
            call.answer();
          }
        } else if (click.action === 'end') {
          if (call) {
            call.hangup();
          }
        }
      })
    );
  }

  get isProjectPhoneOnWeb(): boolean {
    return this.settings.isProjectPhone() && !this.electron.isElectronApp;
  }

  ngOnInit(): void {
    this.phoneService.init();
    window['componentRef'] = {
      zone: this.ngZone,
      componentFn: (value: string) => this.sendTokenToServer(value),
      component: this
    };

    this.addSubscription(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationError) {
          this.loggerService.error('NavigationError', event.error);
          this.router.navigateByUrl('s/404');
          return;
        }
      })
    );
  }

  onNewMessageReceived(data: any): boolean {
    // this.loggerService.log('New message received', data);
    this.toast.showInfo('onNewMessageReceived success');
    return true;
  }

  sendTokenToServer(token: string): boolean {
    const data = {
      appName: 'android',
      token
    };
    this.notificationService.registerToken(data);
    return true;
  }

  broadcastMessage(message: string, params: any): void {
    this.ngZone.run(() => {
      this.messageService.broadcast(message, params);
    });
  }

  checkNotificationPromise(): boolean {
    try {
      if (!('Notification' in window)) {
        throw new Error('No chrome notification');
      }
      Notification.requestPermission().then();
    } catch (e) {
      return false;
    }

    return true;
  }

  askNotificationPermission(): void {
    if (!('Notification' in window)) {
      // this.loggerService.log('This browser does not support notifications.');
      this.loggerService.log('This browser does not support notifications.');
    } else {
      if (!this.checkNotificationPromise()) {
        Notification.requestPermission().then();
      }
    }
  }

  displayInboundCallNotification(call: PhoneCall): void {
    const options = {
      body: `${_ti('call_notification.inbound.title')} ${call.remoteNumber}`,
      icon: 'assets/icons/app-icon.ico',
      vibrate: [100, 50, 100],
      data: {
        dateOfArrival: Date.now(),
        primaryKey: 1
      },
      actions: [
        {
          action: 'answer',
          title: _ti('new_call_alert.action.answer')
        },
        {
          action: 'hangup',
          title: _ti('new_call_alert.action.refuse')
        }
      ]
    };
    this.serviceWorkerService.showNotification(
      `${_ti('call_notification.inbound.title')}: ${call.remoteNumber}`,
      options
    );
  }

  displayOutboundCallNotification(call: PhoneCall): void {
    const options = {
      body: `Click here to open Graam`,
      icon: 'assets/icons/app-icon.ico'
    };
    this.serviceWorkerService.showNotification(
      `${_ti('call_notification.outbound.title')}: ${call.remoteNumber}`,
      options
    );
  }
}
